import { useTranslation } from "react-i18next";
import type { PmOverview } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { phaseLabel, phaseTone } from "./pm-labels.js";
import { StatusChip } from "./StatusChip.js";

type OverviewMs = PmOverview["scenarios"][number]["microScenarios"][number];

/**
 * The board's landing page (ADR-055 phase 5, mockup "1 Landingpage"): every scenario with its micro-scenarios as cards
 * — code, phase, title and the current phase's progress (`done` column / all tasks, D4). A card is a real button, so
 * it is reachable and operable by keyboard; the whole card is the target.
 */
export function PmLanding({ overview, onOpen }: { overview: PmOverview; onOpen: (msId: number) => void }) {
  const { t } = useTranslation();
  return (
    <div className="bg-muted/40 flex-1">
      <div className="page-column flex flex-col gap-8 py-8">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">{t("pmBoard.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("pmBoard.landingIntro")}</p>
        </div>

        {overview.scenarios.every((s) => s.microScenarios.length === 0) ? (
          <p className="text-muted-foreground text-sm">{t("pmBoard.noScenarios")}</p>
        ) : null}

        {overview.scenarios
          .filter((s) => s.microScenarios.length > 0)
          .map((sc) => (
            <section key={sc.id} aria-labelledby={`pm-sc-${sc.id}`} className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <h2 id={`pm-sc-${sc.id}`} className="text-lg font-semibold">
                  {sc.title}
                </h2>
                <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs font-medium tabular-nums">
                  {t("pmBoard.scenarioCount", { count: sc.microScenarios.length })}
                </span>
              </div>
              <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-4 p-0">
                {sc.microScenarios.map((ms) => (
                  <li key={ms.id} className="flex">
                    <MsCard ms={ms} onOpen={() => onOpen(ms.id)} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
      </div>
    </div>
  );
}

function MsCard({ ms, onOpen }: { ms: OverviewMs; onOpen: () => void }) {
  const { t } = useTranslation();
  const { done, total } = ms.progress;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <Button
      variant="outline"
      onPress={onOpen}
      // A card, not a pill: the button skin's single-line defaults are undone here (wrap, top-left, full height).
      className="bg-background text-foreground h-auto w-full flex-col items-stretch gap-3 rounded-xl p-4 text-left whitespace-normal shadow-sm hover:shadow-md"
    >
      <span className="flex items-center gap-2">
        <span className="text-muted-foreground text-xs font-semibold tabular-nums">{ms.code}</span>
        <StatusChip tone={phaseTone(ms.phase)} className="ml-auto">
          {phaseLabel(t, ms)}
        </StatusChip>
      </span>
      <span className="text-[0.95rem] leading-5 font-semibold">{ms.title}</span>
      {/* Decorative: inside a button, a progressbar role is flattened to text anyway — the line below says it. */}
      <span aria-hidden="true" className="bg-border block h-1.5 overflow-hidden rounded-full">
        <span className="bg-primary block h-full" style={{ width: `${pct}%` }} />
      </span>
      <span className="text-muted-foreground text-[0.8rem] tabular-nums">
        {total === 0 ? t("pmBoard.noTasks") : t("pmBoard.progress", { done, total })}
      </span>
    </Button>
  );
}
